import Collapse from '@material-ui/core/Collapse';
import compose from 'recompose/compose';
import has from 'lodash.has';
import KioskLog from 'models/KioskLog';
import React from 'react';
import { actions as categoriesActions } from 'redux/domain/Categories';
import { bindActionCreators } from 'redux';
import { cancelRequest, getCancelTokenSource } from 'api/Helpers';
import { connect } from 'react-redux';
import { getCategoryChildren } from 'shared/utils/categoryHelpers';
import { Link } from 'react-router-dom';
import { List, ListItem, ListItemText } from '@material-ui/core';
import { PropTypes } from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import withRouter from '../../../shared/utils/WithRouter';

// injected into the component using 'withStyles'
const styles = {
	root: {
		marginLeft: '-56px',
		width: 'inherit',
		minWidth: 360,
		maxWidth: 800,
	},
	listItemParent: {
		padding: '10px 10px 10px 12px',
		height: '41px',
	},
	listItemChild: {
		padding: '10px 10px 10px 12px',
		height: '38px',
	},
	typographyParent: {
		lineHeight: '16pt',
		fontSize: '14pt',
	},
	typographyChild: {
		lineHeight: '14pt',
		fontSize: '10pt',
	},
};

class TreeContainer extends React.Component {
	constructor() {
		super();
		this.cancelSource = getCancelTokenSource();
		this.state = {};
		this.handleClick = this.handleClick.bind(this);
	}

	componentDidMount() {
		this.loadCategoriesAsync();
		this.getAllCategoriesAsync();
	}

	componentWillUpdate(nextProps) {
		if (nextProps.fetchError) {
			// had to add this since errorPage was getting rendered multiple times
			if (this.props.router.location.pathname !== '/errorPage') {
				const log = new KioskLog(
					new Error('Fetch Error in TreeContainer'),
					`Referrer URL: ${this.props.router.location.pathname}`,
				);
				log.postMessage();
				this.props.router.navigate('/errorPage', { 
					state: { 
						text: `We couldn't load the category tree.` 
					} 
				});
			}
		}
	}

	componentWillUnmount() {
		// if a request is in progress stop it.
		cancelRequest(this.cancelSource);
	}

	/**
	 * Get a a list of all available categories.
	 */
	getAllCategoriesAsync() {
		const { getAllCategories, updateFetchError } = this.props;
		try {
			getAllCategories(this.cancelSource.token);
		} catch (error) {
			const log = new KioskLog(
				error,
				'Error file location: features/NavBar/containers/TreeContainer',
			);
			log.postMessage();
			updateFetchError(error);
		}
	}

	handleClick = toggleOpenId => () => {
		this.setState({ [toggleOpenId]: !this.state[toggleOpenId] });
	};

	/**
	 * Retreive a list of top level categories.
	 */
	loadCategoriesAsync() {
		const { getTopCategories, updateFetchError } = this.props;
		try {
			getTopCategories(this.cancelSource.token);
		} catch (e) {
			const log = new KioskLog(e);
			log.postMessage();
			updateFetchError(e);
		}
	}

	// determine whether to render a ListItem with a link
	// or a ListItem with child categories
	renderCategories(currentCat) {
		const { classes } = this.props;
		// The following validation will assure that no critical errors or debuggers happen,
		const hasCategoryId = has(currentCat, 'categoryId');
		if (hasCategoryId) {
			// this category object is considered valid
			const { allCategories } = this.props;
			const catChildren = getCategoryChildren(currentCat.categoryId, allCategories);
			return catChildren.length > 0 ? (
				// this category has children
				// on click, it triggers the drop down
				this.renderCatChildren(currentCat, catChildren)
			) : (
				// this category has no children
				// on click, it is a link
				<ListItem
					button
					key={currentCat.categoryId}
					onClick={this.props.closeDrawer}
					component={Link}
					to={`/category/${currentCat.categoryId}`}
					className={classes.listItemChild}
				>
					<ListItemText
						inset
						primary={currentCat.name}
						primaryTypographyProps={{ className: classes.typographyChild }}
					/>
				</ListItem>
			);
		}
		return null;
	}

	// Create a ListItem that is a link to the above category
	// Also render child categories
	renderSubCategories(catId, catChildren) {
		const { classes } = this.props;
		const renderViewAll = (
			<ListItem
				button
				key={`${catId}L`}
				component={Link}
				to={`/category/${catId}`}
				className={classes.listItemChild}
				onClick={this.props.closeDrawer}
			>
				<ListItemText
					inset
					primary="View All"
					primaryTypographyProps={{ className: classes.typographyChild }}
				/>
			</ListItem>
		);
		const childRender = catChildren.map(cat => this.renderCategories(cat));
		childRender.unshift(renderViewAll);
		return childRender;
	}

	// Create a ListItem for a category with children
	renderCatChildren(currentCat, catChildren) {
		const { classes } = this.props;
		const nestedItems = this.renderSubCategories(currentCat.categoryId, catChildren);
		const toggleOpenId = `toggle${currentCat.categoryId}`;
		const open = this.state[toggleOpenId] || false;

		return (
			<div key={currentCat.categoryId}>
				<ListItem
					button
					className={classes.listItemParent}
					onClick={this.handleClick(toggleOpenId)}
				>
					<ListItemText
						inset
						primary={currentCat.name}
						primaryTypographyProps={{ className: classes.typographyParent }}
					/>
				</ListItem>
				<Collapse in={open}>
					<List>
						{nestedItems.map(item => (
							<ListItem key={item.key} style={{ padding: '0px' }}>
								<ListItemText
									inset
									primary={item}
									primaryTypographyProps={{ className: classes.typographyChild }}
									style={{ padding: '0px 0px 0px calc(0% + 18px)' }}
								/>
							</ListItem>
						))}
					</List>
				</Collapse>
			</div>
		);
	}

	render() {
		const { classes, categories } = this.props;
		return (
			<List className={classes.root}>
				{categories.map(currentCat => this.renderCategories(currentCat))}
			</List>
		);
	}
}

TreeContainer.propTypes = {
	allCategories: PropTypes.objectOf(PropTypes.object),
	classes: PropTypes.shape({
		root: PropTypes.string,
		listItemParent: PropTypes.string,
		listItemChild: PropTypes.string,
		typographyParent: PropTypes.string,
		typographyChild: PropTypes.string,
	}).isRequired,
	closeDrawer: PropTypes.func.isRequired,
	getAllCategories: PropTypes.func.isRequired,
	getTopCategories: PropTypes.func.isRequired,
	updateFetchError: PropTypes.func.isRequired,
	categories: PropTypes.arrayOf(
		PropTypes.shape({
			CategoryId: PropTypes.number,
			Name: PropTypes.string,
			PictureUrl: PropTypes.string,
			Description: PropTypes.string,
		}),
	).isRequired,
};

TreeContainer.defaultProps = {
	allCategories: {},
};

const mapStateToProps = state => {
	const allCategories = state.categories.get('allCategories');
	const categories = state.categories.get('categories');
	const isFetching = state.categories.get('isFetching');
	const lastFetchTime = state.categories.get('lastFetchTime');
	const fetchError = state.categories.get('fetchError');
	return {
		allCategories,
		categories,
		isFetching,
		lastFetchTime,
		fetchError,
	};
};

const mapDispatchToProps = dispatch => bindActionCreators(categoriesActions, dispatch);

// for testing purposes
export { TreeContainer as TreeContainerClass };

// connect returns a function that accepts a component
export default withRouter(compose(
	withStyles(styles, { name: 'TreeContainer' }),
	connect(mapStateToProps, mapDispatchToProps),
)(TreeContainer));
